import { useEffect, useRef, useState } from "react"

// Animated mini-mockup for the Ask feature row.
// 11s loop: type question → AI streams response word-by-word with citations →
// [1] flashes (clicked) → source popover with highlighted snippet.
// Styles live under `.ak-*` classes in src/index.css (scoped to .landing-page).

const CHUNKS: { cls: string; text: string; cite?: number }[] = [
  { cls: "ak-c1",  text: "Across" },
  { cls: "ak-c2",  text: " the M5" },
  { cls: "ak-c3",  text: " Bridge" },
  { cls: "ak-c4",  text: " Upgrade ", cite: 1 },
  { cls: "ak-c5",  text: " and Northwind" },
  { cls: "ak-c6",  text: " Rail signalling" },
  { cls: "ak-c7",  text: " renewal ", cite: 2 },
  { cls: "ak-c8",  text: " your team has" },
  { cls: "ak-c9",  text: " maintained zero" },
  { cls: "ak-c10", text: " lost-time incidents" },
  { cls: "ak-c11", text: " over 18 months." },
  { cls: "ak-c12", text: " The 2023 ISO" },
  { cls: "ak-c13", text: " 45001 audit ", cite: 3 },
  { cls: "ak-c14", text: " confirmed all 12" },
  { cls: "ak-c15", text: " active highway sites" },
  { cls: "ak-c16", text: " maintain zero-LTI status." },
]

// Render a chunk: optional leading text, optional citation, optional trailing punctuation.
// We model citations as appearing at the END of their chunk's text (e.g. "Upgrade [1]"),
// matching the preview HTML where the cite is placed at the chunk boundary.
const CHUNK_TRAILING: Record<number, string> = {
  2: ",",  // after [2], we want a comma
}

export function AskVisual() {
  const rootRef = useRef<HTMLDivElement>(null)
  const [inView, setInView] = useState(false)

  useEffect(() => {
    const el = rootRef.current
    if (!el) return
    const obs = new IntersectionObserver(
      (entries) => setInView(entries[0]?.isIntersecting ?? false),
      { threshold: 0.4 },
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [])

  return (
    <div ref={rootRef} className={`ak-frame ${inView ? "ak-playing" : ""}`}>
      {/* Header */}
      <div className="ak-head">
        <span className="ak-head-title">AI Assistant</span>
        <span className="ak-head-meta">Westbrook Highway Upgrade</span>
      </div>

      {/* Body */}
      <div className="ak-body">
        {/* User question bubble */}
        <div className="ak-msg-user">
          What evidence do we have for our zero-LTI claim across highway projects?
        </div>

        {/* AI response */}
        <div className="ak-msg-ai">
          <div className="ak-preamble">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M8 1.5l1.4 3.6 3.6 1.4-3.6 1.4-1.4 3.6-1.4-3.6-3.6-1.4 3.6-1.4z" />
            </svg>
            Based on your library
          </div>
          <p className="ak-resp">
            {CHUNKS.map((c) => (
              <span key={c.cls} className={`ak-chunk ${c.cls}`}>
                {c.text}
                {c.cite && (
                  <span className={`ak-cite ak-c${c.cite}`}>{c.cite}</span>
                )}
                {c.cite && CHUNK_TRAILING[c.cite] ? CHUNK_TRAILING[c.cite] : null}
              </span>
            ))}
          </p>
        </div>

        {/* Source popover */}
        <div className="ak-popover">
          <div className="ak-pop-head">
            <span className="ak-doc-ico">PDF</span>
            <span>M5 Bridge Upgrade — Case Study · p. 12</span>
          </div>
          <p className="ak-pop-snip">
            &ldquo;…we maintained <span className="ak-highlight">zero LTI incidents</span> across all contractor groups, validated by the independent safety auditor at programme close.&rdquo;
          </p>
        </div>
      </div>

      {/* Input area */}
      <div className="ak-input-wrap">
        <span className="ak-ctx-chip">
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M3 3.5a1 1 0 0 1 1-1h7l3 3v7a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z" />
            <path d="M11 2.5v3h3" />
          </svg>
          Westbrook tender + 3 library docs
        </span>

        <div className="ak-input-box">
          <div className="ak-input-typing">
            <span className="ak-input-placeholder">Ask anything…</span>
            <span className="ak-input-text">What evidence do we have for our zero-LTI claim across highway projects?</span>
            <span className="ak-caret" />
          </div>
          <button type="button" className="ak-send-btn" aria-label="Send">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M8 13V3M3 8l5-5 5 5" />
            </svg>
          </button>
        </div>

        <div className="ak-input-tools">
          <button type="button" className="ak-input-tool" aria-label="Attach file">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M11.5 6 6.7 10.8a2 2 0 0 1-2.8-2.8L9.4 2.5a3 3 0 1 1 4.2 4.2L7.5 12.9a4 4 0 0 1-5.7-5.7l5.4-5.4" />
            </svg>
          </button>
          <button type="button" className="ak-input-prompts">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
              <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
            </svg>
            Prompts
          </button>
        </div>
      </div>
    </div>
  )
}
